import { useMemo, useState } from 'react';
import type { UseAutocompleteValueProps } from '../types';

function useAutocompleteValue({
  controlledValue,
  initialValue = '',
  onValueChange,
  onChange,
  fixedItems,
  apiItems,
  placeholder,
  freeSolo,
  allowDeselectOnReselect = false,
}: UseAutocompleteValueProps) {
  // Use controlled value if provided, otherwise use internal state
  const [internalValue, setInternalValue] = useState(initialValue);
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleSelect = (currentValue: string) => {
    // If the selected value is the same as current value and deselect on reselect is disabled, don't clear it
    if (!allowDeselectOnReselect && currentValue === value) {
      return;
    }
    // If the selected value is the same as current value, clear it
    const newValue = currentValue === value ? '' : currentValue;

    // Update internal state if uncontrolled
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }

    // Call appropriate callback
    if (onValueChange) {
      onValueChange(newValue);
    } else if (onChange) {
      onChange(newValue);
    }
  };

  // Get the display label for the selected value
  const displayValue = useMemo(() => {
    if (!value) return placeholder;

    // Check in fixed items first
    const fixedItem = fixedItems.find(item => item.value === value);
    if (fixedItem) return fixedItem.label;

    // Then check in API items
    const apiItem = apiItems.find(item => item.value === value);
    if (apiItem) return apiItem.label;

    // If freeSolo is enabled, return the value itself
    return freeSolo ? value : placeholder;
  }, [value, fixedItems, apiItems, placeholder, freeSolo]);

  return {
    value,
    displayValue,
    handleSelect,
  };
}

export { useAutocompleteValue };
